interface NewsArticle {
  title: string;
  description: string;
  url: string;
  sourceUrl?: string;
  image?: string;
  publishedAt: string;
  source: string;
}

interface NewsResponse {
  articles: NewsArticle[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

class NewsService {
  async fetchNews(page: number = 1, pageSize: number = 10): Promise<NewsResponse> {
    try {
      // Busca notícias do endpoint do BFF
      const baseURL = process.env.REACT_APP_BFF_URL || 'http://localhost:8080';
      const response = await fetch(`${baseURL}/news?page=${page}&pageSize=${pageSize}`);
      
      if (!response.ok) {
        throw new Error('Erro ao buscar notícias');
      }

      const data: NewsResponse = await response.json();
      
      console.log(`${data.articles.length} notícias carregadas (página ${page}/${data.totalPages})`);
      
      return data;
    } catch (error) {
      console.error('Erro ao buscar notícias:', error);
      // Em caso de erro, retorna notícias mock
      console.log('Usando notícias mock como fallback');
      return {
        articles: this.getMockNews().slice((page - 1) * pageSize, page * pageSize),
        total: this.getMockNews().length,
        page,
        pageSize,
        totalPages: Math.ceil(this.getMockNews().length / pageSize),
      };
    }
  }

  private getMockNews(): NewsArticle[] {
    const newsData = [
      {
        title: 'A Importância da Saúde Mental no Trabalho',
        description: 'Estudos recentes mostram que o cuidado com a saúde mental dos colaboradores aumenta a produtividade e reduz o absenteísmo nas empresas. Especialistas recomendam programas de apoio psicológico.',
        url: 'https://www.who.int/mental_health/workplace/pt/',
        image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&h=400&fit=crop',
        publishedAt: new Date().toISOString(),
        source: 'Psicologia Hoje'
      },
      {
        title: 'Terapia Cognitivo-Comportamental: Como Funciona',
        description: 'A TCC é uma das abordagens mais eficazes no tratamento de ansiedade e depressão. Entenda como funciona essa técnica terapêutica baseada em evidências científicas.',
        url: 'https://www.scielo.br',
        image: 'https://images.unsplash.com/photo-1527689368864-3a821dbccc34?w=800&h=400&fit=crop',
        publishedAt: new Date(Date.now() - 86400000).toISOString(),
        source: 'Portal de Psicologia'
      },
      {
        title: 'Mindfulness e Seus Benefícios para o Bem-Estar',
        description: 'A prática de mindfulness tem ganhado destaque como ferramenta para redução do estresse e melhoria da qualidade de vida. Pesquisas comprovam sua eficácia.',
        url: 'https://www.apa.org',
        image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&h=400&fit=crop',
        publishedAt: new Date(Date.now() - 172800000).toISOString(),
        source: 'Bem-Estar Mental'
      },
      {
        title: 'Desenvolvimento Infantil: Marcos Importantes',
        description: 'Conheça os principais marcos do desenvolvimento infantil e saiba quando buscar ajuda profissional. A intervenção precoce faz toda a diferença.',
        url: 'https://www.sbp.com.br',
        image: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800&h=400&fit=crop',
        publishedAt: new Date(Date.now() - 259200000).toISOString(),
        source: 'Psicologia Infantil'
      },
      {
        title: 'Resiliência Emocional: Como Desenvolver',
        description: 'Aprenda estratégias práticas para desenvolver resiliência emocional e lidar melhor com os desafios da vida. A psicologia positiva pode ajudar.',
        url: 'https://www.positive-psychology.com',
        image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800&h=400&fit=crop',
        publishedAt: new Date(Date.now() - 345600000).toISOString(),
        source: 'Mente Saudável'
      },
      {
        title: 'Burnout: Como Reconhecer e Prevenir',
        description: 'O esgotamento profissional afeta milhões de trabalhadores. Aprenda a identificar os sintomas e descobra estratégias de prevenção eficazes.',
        url: 'https://www.who.int',
        image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&h=400&fit=crop',
        publishedAt: new Date(Date.now() - 432000000).toISOString(),
        source: 'Saúde Ocupacional'
      },
      {
        title: 'Inteligência Emocional no Século XXI',
        description: 'Desenvolver a inteligência emocional tornou-se essencial para o sucesso pessoal e profissional. Conheça as 5 competências fundamentais.',
        url: 'https://www.danielgoleman.info',
        image: 'https://images.unsplash.com/photo-1516534775068-ba3e7458af70?w=800&h=400&fit=crop',
        publishedAt: new Date(Date.now() - 518400000).toISOString(),
        source: 'Desenvolvimento Pessoal'
      },
      {
        title: 'Terapia Online: O Futuro da Psicologia?',
        description: 'A telepsicologia cresce exponencialmente. Estudos mostram eficácia equivalente ao atendimento presencial para diversos transtornos.',
        url: 'https://site.cfp.org.br',
        image: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=800&h=400&fit=crop',
        publishedAt: new Date(Date.now() - 604800000).toISOString(),
        source: 'Inovação em Saúde'
      }
    ];
    
    // Retorna notícias aleatórias a cada chamada para variar
    return newsData.sort(() => Math.random() - 0.5);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Hoje';
    } else if (diffDays === 1) {
      return 'Ontem';
    } else if (diffDays < 7) {
      return `${diffDays} dias atrás`;
    } else {
      return date.toLocaleDateString('pt-BR', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    }
  }
}

const newsService = new NewsService();
export default newsService;
export type { NewsArticle, NewsResponse };
